import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Link } from "react-router-dom";

import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { cn } from "@/lib/utils";

import { fetchQualityRmas, type QualityRma } from "./rmaApi";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function rmaChipLabel(row: Pick<QualityRma, "id" | "rmaNumber">): string {
  return row.rmaNumber?.trim() || `RMA ${row.id}`;
}

function toSearchOption(row: QualityRma): SearchableSelectOption {
  return {
    value: String(row.id),
    label: rmaChipLabel(row),
    description: [row.customerName?.trim(), row.complaint?.trim()].filter(Boolean).join(" · ") || null,
  };
}

export function QualityRmaPicker({
  id,
  values,
  knownLabels,
  disabled,
  onChange,
}: {
  id: string;
  values: number[];
  knownLabels?: Record<number, string | null | undefined>;
  disabled?: boolean;
  onChange: (rmaIds: number[]) => void;
}): JSX.Element {
  const [searchValue, setSearchValue] = useState("");
  const [knownRows, setKnownRows] = useState<Map<number, QualityRma>>(new Map());
  const debouncedSearch = useDebouncedValue(searchValue.trim(), 250);
  const searchQuery = debouncedSearch.length >= 2 ? debouncedSearch : "";

  const searchResultsQuery = useQuery({
    queryKey: ["quality", "rma-quick-search", searchQuery],
    queryFn: () => fetchQualityRmas({ search: searchQuery || undefined, limit: 20 }),
    enabled: searchQuery.length >= 2 || searchQuery === "",
    staleTime: 20_000,
  });

  useEffect(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    if (rows.length === 0) {
      return;
    }
    setKnownRows((current) => {
      const next = new Map(current);
      for (const row of rows) {
        next.set(row.id, row);
      }
      return next;
    });
  }, [searchResultsQuery.data?.rows]);

  const selectedRows = values.map((rmaId) => ({
    id: rmaId,
    chipLabel:
      knownRows.get(rmaId) != null
        ? rmaChipLabel(knownRows.get(rmaId)!)
        : knownLabels?.[rmaId]?.trim() || `RMA ${rmaId}`,
  }));

  const options = useMemo(() => {
    const rows = searchResultsQuery.data?.rows ?? [];
    return rows.filter((row) => !values.includes(row.id)).map(toSearchOption);
  }, [searchResultsQuery.data?.rows, values]);

  return (
    <div className="space-y-1.5">
      {selectedRows.length > 0 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {selectedRows.map((selected) => (
            <span
              key={selected.id}
              className="inline-flex max-w-full items-center gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800"
            >
              <Link className="text-xs text-sky-700 hover:underline" to={`/quality/rmas/${selected.id}`}>
                {selected.chipLabel}
              </Link>
              <button
                type="button"
                className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
                aria-label={`Remove ${selected.chipLabel}`}
                disabled={disabled}
                onClick={() => onChange(values.filter((rmaId) => rmaId !== selected.id))}
              >
                <X className="h-3 w-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <SearchableSelect
        id={id}
        value=""
        onValueChange={(option) => {
          if (option == null) {
            return;
          }
          const parsed = Number(option.value);
          if (!Number.isFinite(parsed) || parsed < 1 || values.includes(parsed)) {
            return;
          }
          onChange([...values, parsed]);
          setSearchValue("");
        }}
        options={options}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        placeholder="Search RMA number or customer"
        searchPlaceholder="Search RMA number or customer"
        emptyMessage={
          searchValue.trim().length < 2
            ? "Type at least two characters to search RMAs, or browse recent RMAs."
            : "No RMAs match."
        }
        isLoading={searchResultsQuery.isFetching}
        disabled={disabled}
        panelMinWidth={320}
        className={cn(values.length > 0 ? "mt-0" : undefined)}
      />
    </div>
  );
}
